/**
 * useBackButton hook - Browser history integration for the session flow.
 *
 * Handles:
 * - Pushing state to browser history at key step transitions
 * - Listening for popstate (browser back/forward) events
 * - Showing confirmation dialogs when backing out of critical states
 * - Consistent behaviour between in-app and browser back buttons
 *
 * The hook pushes a history entry each time the step changes.
 * When the browser back button is pressed, it invokes the onBack
 * callback with the step the user is trying to leave. The parent
 * component decides whether to allow or block the navigation.
 */

import { useEffect, useRef, useCallback } from 'react'
import type { SessionFlowStep } from '@/pages/session/SessionFlow'

/**
 * Steps where going back requires confirmation because work could be lost.
 */
const CONFIRM_BEFORE_BACK: SessionFlowStep[] = [
  'generating',
  'generating_images',
  'image_refinement',
]

/**
 * Steps where back navigation is completely blocked (nowhere to go).
 */
const BLOCK_BACK: SessionFlowStep[] = ['start']

/**
 * Ordered steps for determining the "previous" step in the linear flow.
 */
const STEP_ORDER: SessionFlowStep[] = [
  'start',
  'config',
  'consent',
  'mic_test',
  'conversation',
  'photo_choice',
  'generating',
  'generating_images',
  'image_selection',
  'image_refinement',
  'image',
  'complete',
]

export interface UseBackButtonOptions {
  /** Current step in the session flow */
  currentStep: SessionFlowStep
  /** Whether image generation is actively in progress */
  isGenerating: boolean
  /**
   * Callback invoked when the user presses browser back.
   * Receives the step the user is leaving.
   * Return `true` to allow navigation, `false` to block it.
   */
  onBack: (fromStep: SessionFlowStep) => boolean
  /**
   * Callback invoked when back navigation needs confirmation
   * (e.g., during image generation). The parent should show a dialog.
   */
  onConfirmNeeded: (fromStep: SessionFlowStep) => void
}

export interface UseBackButtonReturn {
  /** The previous step in the flow, or null if there is none. */
  previousStep: SessionFlowStep | null
  /** Whether the current step supports going back. */
  canGoBack: boolean
  /** Programmatic trigger for back navigation. Returns true if allowed. */
  goBack: () => boolean
}

/**
 * Hook for managing browser back button behaviour within the session flow.
 *
 * Integrates with the browser History API to ensure the browser back button
 * and in-app back buttons behave identically.
 */
export function useBackButton({
  currentStep,
  isGenerating,
  onBack,
  onConfirmNeeded,
}: UseBackButtonOptions): UseBackButtonReturn {
  const isInternalNavigation = useRef(false)
  const previousStepRef = useRef<SessionFlowStep>(currentStep)
  const initialisedRef = useRef(false)

  // Compute the previous step in the linear flow
  const previousStep: SessionFlowStep | null = (() => {
    const index = STEP_ORDER.indexOf(currentStep)
    if (index <= 0) return null
    return STEP_ORDER[index - 1] ?? null
  })()

  const canGoBack = !BLOCK_BACK.includes(currentStep) && previousStep !== null

  // Shared back navigation logic
  const handleBackNavigation = useCallback(
    (fromStep: SessionFlowStep): boolean => {
      if (BLOCK_BACK.includes(fromStep)) {
        return false
      }

      if (isGenerating || CONFIRM_BEFORE_BACK.includes(fromStep)) {
        onConfirmNeeded(fromStep)
        return false
      }

      return onBack(fromStep)
    },
    [isGenerating, onBack, onConfirmNeeded]
  )

  const pushHistoryState = useCallback((step: SessionFlowStep) => {
    window.history.pushState({ step, source: 'future-ideas' }, '', '')
  }, [])

  const replaceHistoryState = useCallback((step: SessionFlowStep) => {
    window.history.replaceState({ step, source: 'future-ideas' }, '', '')
  }, [])

  // Push initial history state on mount
  useEffect(() => {
    if (!initialisedRef.current) {
      replaceHistoryState(currentStep)
      initialisedRef.current = true
    }
  }, [currentStep, replaceHistoryState])

  // Push new history entry when step changes
  useEffect(() => {
    if (previousStepRef.current !== currentStep && initialisedRef.current) {
      if (!isInternalNavigation.current) {
        pushHistoryState(currentStep)
      }
      isInternalNavigation.current = false
      previousStepRef.current = currentStep
    }
  }, [currentStep, pushHistoryState])

  // Listen for popstate (browser back/forward button)
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      const allowed = handleBackNavigation(currentStep)

      if (!allowed) {
        // Block navigation - push current state back onto the stack
        pushHistoryState(currentStep)
      } else {
        // Navigation allowed - mark as internal so we don't double-push
        isInternalNavigation.current = true
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [currentStep, handleBackNavigation, pushHistoryState])

  // Programmatic back navigation (for in-app back buttons)
  const goBack = useCallback((): boolean => {
    return handleBackNavigation(currentStep)
  }, [currentStep, handleBackNavigation])

  return {
    previousStep,
    canGoBack,
    goBack,
  }
}

export default useBackButton
